import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Mail, MessageSquareText, X } from 'lucide-react';
import { COOKIE_CONSENT_EVENT, getCookieConsent } from './CookieConsentBanner';

const WHATSAPP_NUMBER = '201012757416';
const CONTACT_EMAIL = 'contact@al-itqan-academy.com';
const HIDDEN_PREFIXES = ['/admin', '/dashboard', '/teacher', '/login', '/register'];

// Menu de contact flottant : WhatsApp, email et formulaire — tout passe par
// l'administration, jamais un contact direct avec un professeur en particulier.
export function FloatingContactButton() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isRtl = i18n.language === 'ar';

  useEffect(() => {
    setCookieBannerVisible(!getCookieConsent());
    const handler = () => setCookieBannerVisible(!getCookieConsent());
    window.addEventListener(COOKIE_CONSENT_EVENT, handler);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) {
    return null;
  }

  const whatsappMessage = t('whatsappButton.message');
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
  const positionClass = isRtl ? 'left-5' : 'right-5';
  const originClass = isRtl ? 'origin-bottom-left' : 'origin-bottom-right';

  return (
    <div
      ref={menuRef}
      className={`fixed z-30 flex flex-col items-end gap-3 ${positionClass} ${
        cookieBannerVisible ? 'bottom-24 sm:bottom-20' : 'bottom-5'
      }`}
    >
      {isOpen && (
        <div
          className={`flex flex-col items-stretch gap-2 rounded-[24px] bg-manuscript-card p-2.5 shadow-xl shadow-manuscript-ink/15 ring-1 ring-manuscript-line ${originClass}`}
        >
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-semibold text-manuscript-ink transition-colors hover:bg-paper-200"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white">
              <MessageCircle className="h-4 w-4" aria-hidden />
            </span>
            {t('floatingContact.whatsapp')}
          </a>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-semibold text-manuscript-ink transition-colors hover:bg-paper-200"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-seal-500/10 text-seal-500">
              <Mail className="h-4 w-4" aria-hidden />
            </span>
            {t('floatingContact.email')}
          </a>
          <Link
            to="/contact"
            className="flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-semibold text-manuscript-ink transition-colors hover:bg-paper-200"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-manuscript-gold-400/15 text-manuscript-gold-500">
              <MessageSquareText className="h-4 w-4" aria-hidden />
            </span>
            {t('floatingContact.form')}
          </Link>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={t(isOpen ? 'floatingContact.close' : 'floatingContact.open')}
        aria-expanded={isOpen}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-seal-500 text-paper-50 shadow-lg shadow-seal-500/30 transition-all hover:scale-105"
      >
        {isOpen ? <X className="h-6 w-6" aria-hidden /> : <MessageCircle className="h-6 w-6" aria-hidden />}
      </button>
    </div>
  );
}
